import { injectIntl } from "react-intl";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "src/services/api.service";
import SMSCreativeTable from "./SMSCreativeTable";
import EmailCreativeTable from "./EmailCreativeTable";
import ListsTable from "./ListsTable";
import NewCreative from "./NewCreative";
import { OperationType } from "src/helpers/Enums";
import OfferDelete from "./OfferDelete";
import ResetScore from "./ResetScore";
import CreativeDelete from "./CreativeDelete";
import NewOffer from "./NewOffer";
import AISuggestCreativeModal from "./AISuggestCreativeModal";
import Pagination from "src/components/Pagination";

const Details = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateData = location.state || { type: "pending" };
  const limit = 10;
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [smsCreatives, setSmsCreatives] = useState([]);
  const [emailCreatives, setEmailCreatives] = useState([]);
  const [lists, setLists] = useState([]);
  const [selectedTab, setSelectedTab] = useState("email");
  const [offerDetails, setOfferDetails] = useState(null);

  // Determine problemType based on suggestions status
  const determineProblemType = (suggestions) => {
    if (!suggestions || suggestions.length === 0) {
      return "resolved";
    }

    const hasPending = suggestions.some(
      (suggestion) => suggestion?.status === "pending"
    );
    if (hasPending) {
      return "pending";
    }

    const allNoted = suggestions.every(
      (suggestion) => suggestion?.status === "noted"
    );
    if (allNoted) {
      return "noted";
    }

    return "resolved";
  };

  const [problemType, setProblemType] = useState(() => {
    return stateData.type || "pending";
  });
  const offerId = location.state?.offerId || location.state?.offer?._id;
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const [creativeOperationType, setCreativeOperationType] = useState(
    OperationType.UPDATE
  );
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [weeklyScores, setWeeklyScores] = useState([]);
  const chartContainerRef = useRef(null);
  const [chartAspectRatio, setChartAspectRatio] = useState(1);
  const [isOfferDeleteOpen, setIsOfferDeleteOpen] = useState(false);
  const [isResetScoreOpen, setIsResetScoreOpen] = useState(false);
  const [isBoosted, setIsBoosted] = useState(false);
  const [isCreativeDeleteOpen, setIsCreativeDeleteOpen] = useState(false);
  const [creativeId, setCreativeId] = useState(null);
  const [isOfferEditOpen, setIsOfferEditOpen] = useState(false);
  const [isAISuggestOpen, setIsAISuggestOpen] = useState(false);

  const applyOfferSuggestionResponse = (response) => {
    const details = response?.data?.data[0] || [];
    setOfferDetails(details);
    setSmsCreatives(response?.data?.data[0]?.creative_list?.sms || []);
    setEmailCreatives(response?.data?.data[0]?.creative_list?.email || []);
    setLists(details?.used_lists_last_month || []);
    setPage(response?.data?.pagination?.page || 1);
    setTotalPages(response?.data?.pagination?.totalPages || 1);

    const newProblemType = determineProblemType(details?.suggestions);
    setProblemType(newProblemType);
  };

  const refetchOfferDetails = () =>
    apiService
      .getOfferSuggestionDetails({ page, limit, offerId })
      .then(applyOfferSuggestionResponse);

  const refetchWeeklyScores = () => {
    apiService
      .getWeeklyScores(offerId)
      .then((response) => {
        setWeeklyScores(response.data || []);
      })
      .catch(() => {
        setWeeklyScores([]);
      });
  };

  const refetchAllData = () => {
    refetchOfferDetails();
    refetchWeeklyScores();
  };

  useEffect(() => {
    refetchWeeklyScores();
  }, [offerId]);

  useEffect(() => {
    refetchOfferDetails().catch(() => {
      setSmsCreatives([]);
      setEmailCreatives([]);
      setLists([]);
      setOfferDetails(null);
    });
  }, [page, problemType, limit, offerId]);

  const handlePageChange = (page) => {
    setPage(page);
  };

  const closeModal = () => {
    setIsNewCreativeOpen(false);
    setIsOfferDeleteOpen(false);
    setIsResetScoreOpen(false);
    setIsCreativeDeleteOpen(false);
    setIsOfferEditOpen(false);
    setIsAISuggestOpen(false);
    setSelectedCreative(null);
    setCreativeOperationType(OperationType.CREATE);
  };

  const closeModalAndRefresh = () => {
    closeModal();
    refetchAllData();
  };

  const handleOfferDeleteSuccess = () => {
    closeModal();
    navigate("/offers");
  };

  const handleBackToOffers = () => {
    if (location.state?.offersReturnSearch !== undefined) {
      navigate(`/offers${location.state.offersReturnSearch}`);
      return;
    }
    navigate(-1);
  };

  const handleOpenCreativeDelete = (id) => {
    setCreativeId(id);
    setIsCreativeDeleteOpen(true);
  };

  const handleOpenNewCreative = (type, creative = null) => {
    setCreativeOperationType(type);
    setSelectedCreative(creative);
    setIsNewCreativeOpen(true);
  };

  const handleResolved = () => {
    const offer = offerDetails?.offer;
    if (!offer) return;
    apiService
      .updateOffer({ ...offer, is_resolved: true })
      .then(() => refetchAllData());
  };

  const handleNoted = () => {
    const offer = offerDetails?.offer;
    if (!offer) return;
    apiService
      .updateOffer({ ...offer, is_noted: true })
      .then(() => refetchAllData());
  };

  // Same as the two handlers above, but scoped to a single creative: only the
  // suggestions raised for that creative are resolved or noted.
  const handleCreativeResolved = (creative) => {
    if (!creative?._id) return;
    apiService
      .updateCreative({ ...creative, is_resolved: true })
      .then(() => refetchAllData());
  };

  const handleCreativeNoted = (creative) => {
    if (!creative?._id) return;
    apiService
      .updateCreative({ ...creative, is_noted: true })
      .then(() => refetchAllData());
  };

  const openToastWithType = (
    type,
    message,
    autoClose = 3000,
    theme = "light"
  ) => {
    const icon =
      type === "success" ? (
        <img
          src="/media/icons/complete.svg"
          alt="success"
          style={{ width: "14px", height: "14px" }}
        />
      ) : undefined;
    const toastId = toast(message, {
      toastId: `toast-${Date.now()}-${Math.random()}`, // Unique ID for each toast
      type: type,
      position: "bottom-right",
      autoClose: autoClose || 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: theme,
      icon: icon,
    });

    // Backup: Manually dismiss toast after autoClose time to ensure it closes
    if (autoClose && autoClose > 0) {
      setTimeout(() => {
        toast.dismiss(toastId);
      }, autoClose);
    }

    return toastId;
  };

  const handleBoostOffer = () => {
    if (!offerId) return;
    setIsBoosted(true);
    apiService.boostOffer(offerId).then(() => {
      openToastWithType("success", "10% Boost has been applied.");
      refetchAllData();
    });
  };

  const updateOffer = () => {
    const offer = offerDetails?.offer;
    if (!offer) return;
    apiService
      .updateOffer({ ...offer, is_enabled: !offer?.is_enabled })
      .then(() => refetchAllData());
  };

  // Format weekly scores data for chart
  const chartData = useMemo(() => {
    if (!weeklyScores || weeklyScores?.length === 0) return [];
    return weeklyScores
      .sort((a, b) => {
        // Sort by year first, then by week
        if (a.year !== b.year) return a.year - b.year;
        return a.week - b.week;
      })
      .map((item) => ({
        week: `W${item.week}`,
        score: item.average_score,
        fullData: item,
      }));
  }, [weeklyScores]);

  // Calculate aspect ratio for perfect circles
  useEffect(() => {
    const updateAspectRatio = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        const height = chartContainerRef.current.offsetHeight;
        if (width > 0 && height > 0) {
          setChartAspectRatio(width / height);
        }
      }
    };

    updateAspectRatio();
    window.addEventListener("resize", updateAspectRatio);
    return () => window.removeEventListener("resize", updateAspectRatio);
  }, [chartData]);

  const CircularProgress = ({ score, size = 40, color = "#DD2E2E" }) => {
    const radius = size / 2 - 4; // 4px stroke width için padding
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const strokeWidth = 4;

    // Format score: if whole number, show without decimal; if decimal, show 1 decimal place with comma
    const formatScore = (value) => {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value;

      // Check if it's a whole number
      if (Number.isInteger(numValue)) {
        return numValue.toString();
      }

      // If decimal, show 1 decimal place and replace dot with comma
      return numValue.toFixed(1).replace(".", ",");
    };

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="white"
            stroke="#FFF"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div
          className="absolute inset-0 flex items-center justify-center text-[#15151F] text-center text-xs font-bold"
          style={{ fontFamily: "Inter" }}
        >
          {formatScore(score)}
        </div>
      </div>
    );
  };

  return (
    <div
      className={
        "flex flex-col overflow-hidden w-full items-center gap-6 ml-[256px]"
      }
      style={{ height: "100vh" }}
    >
      <div className={"PageHeader z-[1]"}>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBackToOffers}
            className="flex w-[80px] h-[36px] px-3 justify-center items-center gap-1.5 rounded-lg border border-[#E5E7F0] bg-white text-[#15151F] text-xs font-semibold leading-normal"
            style={{
              boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)",
              fontFamily: "Inter",
            }}
          >
            <img
              alt={"Logo"}
              src={"/media/icons/arrowLeft.svg"}
              className={"w-4 h-4"}
            />
            Back
          </button>
          <div className={"PageHeaderTitle"}>Details</div>
        </div>
      </div>
      {problemType !== "resolved" && (
        <div className=" w-full px-6">
          <div
            className={`flex w-full p-3 justify-between items-center rounded-lg ${
              problemType === "pending" ? "bg-[#FFD2D2]" : "bg-[#FFF4D3]"
            }`}
          >
            <div className="flex items-center gap-2">
              <img
                alt={"Logo"}
                src={
                  problemType === "pending"
                    ? "/media/icons/pending.svg"
                    : problemType === "noted"
                    ? "/media/icons/Solid-warning.svg"
                    : "/media/icons/Solid-check.svg"
                }
                className={"w-4 h-4"}
              />
              <div className="flex flex-col justify-center items-start gap-[6px]">
                <div
                  className={`${
                    problemType === "pending"
                      ? "text-[#810D0D]"
                      : problemType === "noted"
                      ? "text-[#7D510E]"
                      : "text-[#0D650C]"
                  } text-[13px] font-bold`}
                >
                  {offerDetails?.suggestions?.length || 0}{" "}
                  {problemType === "pending"
                    ? "Pending Resolution"
                    : problemType === "noted"
                    ? "Noted"
                    : "Resolved"}
                </div>
                {offerDetails?.suggestions &&
                  offerDetails.suggestions.length > 0 && (
                    <div className="flex flex-wrap items-center gap-[6px]">
                      {Array.from(
                        new Set(
                          offerDetails.suggestions
                            .map((suggestion) => suggestion.note)
                            .filter((note) => note)
                        )
                      ).map((uniqueNote, index, array) => (
                        <React.Fragment key={index}>
                          <span
                            className={`${
                              problemType === "pending"
                                ? "text-[#810D0D]"
                                : problemType === "noted"
                                ? "text-[#7D510E]"
                                : "text-[#0D650C]"
                            } text-xs leading-[18px] font-normal whitespace-nowrap`}
                            style={{ fontFamily: "Inter" }}
                          >
                            {uniqueNote}
                          </span>
                          {index < array.length - 1 && (
                            <span
                              className={`${
                                problemType === "pending"
                                  ? "text-[#810D0D]"
                                  : problemType === "noted"
                                  ? "text-[#7D510E]"
                                  : "text-[#0D650C]"
                              } text-xs`}
                              style={{ fontFamily: "Inter" }}
                            >
                              •
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
              </div>
            </div>
            <div className="flex items-center gap-[6px]">
              <button
                onClick={handleResolved}
                className="flex h-[30px] px-3 justify-center items-center gap-1 flex-1 rounded-lg border border-[#E5E7F0] bg-white"
              >
                <img
                  alt={"Logo"}
                  src={"/media/icons/check-circle.svg"}
                  className={"w-4 h-4"}
                />
                <div
                  className="text-[#15151F] text-xs font-semibold leading-normal"
                  style={{ fontFamily: "Inter" }}
                >
                  Resolved
                </div>
              </button>
              <button
                onClick={handleNoted}
                className="flex h-[30px] px-3 justify-center items-center gap-1 flex-1 rounded-lg border border-[#E5E7F0] bg-white"
              >
                <img
                  alt={"Logo"}
                  src={"/media/icons/edit-05.svg"}
                  className={"w-4 h-4"}
                />
                <div
                  className="text-[#15151F] text-xs font-semibold leading-normal"
                  style={{ fontFamily: "Inter" }}
                >
                  Noted
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex pl-6 pr-[38px] gap-[99px] w-full items-start self-stretch">
        <div className="flex flex-col items-start gap-5 flex-shrink-0">
          <div className="flex px-[10px] items-start gap-[10px]">
            <div
              className="text-[#15151F] text-2xl font-bold truncate"
              style={{
                fontFamily: "Inter",
                width: 420,
                maxWidth: 420,
                lineHeight: "1.2",
                paddingBottom: "2px",
              }}
            >
              {offerDetails?.offer?.name}
            </div>
            <div
              className={`inline-flex items-center h-[18px] px-[6px] rounded-[34px] ${
                problemType === "pending"
                  ? "bg-[#DD2E2E]"
                  : problemType === "noted"
                  ? "bg-[#F0970B]"
                  : "bg-[#53A852]"
              } text-white text-[11px] font-semibold`}
              style={{ fontFamily: "Inter" }}
            >
              ${offerDetails?.offer?.totalRevenue}
            </div>
          </div>
          <div className="flex px-[10px] items-center gap-4 self-stretch">
            <CircularProgress
              score={offerDetails?.offer?.score}
              size={60}
              color={
                problemType === "pending"
                  ? "#DD2E2E"
                  : problemType === "noted"
                  ? "#F0970B"
                  : "#53A852"
              }
            />
            <div className="w-[1px] self-stretch rounded bg-[#E5E7F0]"></div>
            <div className="flex flex-col items-center gap-2">
              <div
                className="text-[#15151F] text-[11px] font-semibold leading-normal"
                style={{
                  fontFamily: "Inter",
                  leadingTrim: "both",
                  textEdge: "cap",
                }}
              >
                SMS Score
              </div>
              <CircularProgress
                score={offerDetails?.offer?.smsScore}
                size={40}
                color={
                  problemType === "pending"
                    ? "#DD2E2E"
                    : problemType === "noted"
                    ? "#F0970B"
                    : "#53A852"
                }
              />
            </div>
            <div className="w-[1px] self-stretch rounded bg-[#E5E7F0]"></div>
            <div className="flex flex-col items-center gap-2">
              <div
                className="text-[#15151F] text-[11px] font-semibold leading-normal"
                style={{
                  fontFamily: "Inter",
                  leadingTrim: "both",
                  textEdge: "cap",
                }}
              >
                Email Score
              </div>
              <CircularProgress
                score={offerDetails?.offer?.emailScore}
                size={40}
                color={
                  problemType === "pending"
                    ? "#DD2E2E"
                    : problemType === "noted"
                    ? "#F0970B"
                    : "#53A852"
                }
              />
            </div>
          </div>
          <div className="flex px-[10px] flex-col items-start gap-5">
            <div className="h-[1px] self-stretch rounded bg-[#E5E7F0]"></div>
            <div className="flex px-[10px] items-center gap-5 self-stretch">
              {/* <div className="flex flex-col items-start gap-[7px]">
                <div
                  className="text-[#15151F] text-[13px] font-semibold leading-normal"
                  style={{ fontFamily: "Inter" }}
                >
                  Great
                </div>
                <div className="flex items-center gap-[6px] self-stretch">
                  <img
                    alt={"Logo"}
                    src={"/media/icons/Solid-check.svg"}
                    className={"w-4 h-4"}
                  />
                  <div
                    className="text-[#8185A8] text-[13px] font-medium leading-normal"
                    style={{ fontFamily: "Inter" }}
                  >
                    Above 90
                  </div>
                </div>
              </div> 
              <div className="w-[1px] self-stretch rounded bg-[#E5E7F0]"></div>*/}
              <div className="flex flex-col items-start gap-[7px]">
                <div
                  className="text-[#15151F] text-[13px] font-semibold leading-normal"
                  style={{ fontFamily: "Inter" }}
                >
                  Creative
                </div>
                <div
                  className="text-[#8185A8] text-[13px] font-medium leading-normal"
                  style={{ fontFamily: "Inter" }}
                >
                  {offerDetails?.creatives?.length}
                </div>
              </div>
              <div className="w-[1px] self-stretch rounded bg-[#E5E7F0]"></div>
              <div className="flex flex-col items-start gap-[7px]">
                <div
                  className="text-[#15151F] text-[13px] font-semibold leading-normal"
                  style={{ fontFamily: "Inter" }}
                >
                  Lists
                </div>
                <div
                  className="text-[#8185A8] text-[13px] font-medium leading-normal"
                  style={{ fontFamily: "Inter" }}
                >
                  {offerDetails?.used_lists_last_month?.length}
                </div>
              </div>
            </div>
          </div>
          <div className="flex">
            <button
              onClick={() => handleOpenNewCreative(OperationType.CREATE)}
              className="flex h-[36px] px-3 justify-center items-center gap-1 rounded-l-lg border border-[#E5E7F0] bg-white"
              style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/plusIconGray.svg"}
                className={"w-[14px] h-[14px]"}
              />
              <div
                className="text-[#15151F] text-xs font-semibold leading-normal"
                style={{ fontFamily: "Inter" }}
              >
                New Creative
              </div>
            </button>
            <button
              onClick={() => setIsAISuggestOpen(true)}
              className="flex h-[36px] px-3 justify-center items-center gap-1 border-b border-t border-r border-[#E5E7F0] bg-white"
              style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
            >
              <img
                alt={"AI"}
                src={"/media/icons/stars-01-purple.svg"}
                className={"w-[14px] h-[14px]"}
              />
              <div
                className="text-[#7F56D9] text-xs font-semibold leading-normal"
                style={{ fontFamily: "Inter" }}
              >
                AI Suggest
              </div>
            </button>
            <button
              onClick={() => setIsResetScoreOpen(true)}
              className="flex h-[36px] px-3 justify-center items-center gap-1 border-b border-t border-r border-[#E5E7F0] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/refresh-cw-01.svg"}
                className={"w-[14px] h-[14px]"}
              />
              <div
                className="text-[#15151F] text-xs font-semibold leading-normal"
                style={{ fontFamily: "Inter" }}
              >
                Reset Score
              </div>
            </button>
            <button
              onClick={handleBoostOffer}
              disabled={isBoosted}
              className="flex h-[36px] px-3 justify-center items-center gap-1 border-b border-t border-[#E5E7F0] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/arrows-up.svg"}
                className={"w-[14px] h-[14px]"}
              />
              <div
                className="text-[#15151F] text-xs font-semibold leading-normal"
                style={{ fontFamily: "Inter" }}
              >
                Boost
              </div>
            </button>
            <button
              onClick={updateOffer}
              className="flex h-[36px] px-3 justify-center items-center border border-[#E5E7F0]"
              style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
            >
              <img
                alt={"Logo"}
                src={
                  offerDetails?.offer?.is_enabled
                    ? "/media/icons/pauseCircle.svg"
                    : "/media/icons/playCircle.svg"
                }
                className={"w-4 h-4"}
              />
            </button>
            <button
              className="flex h-[36px] px-3 justify-center items-center border-b border-t border-[#E5E7F0]"
              style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
              onClick={() => setIsOfferEditOpen(true)}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/editIcon.svg"}
                className={"w-4 h-4"}
              />
            </button>
            <button
              className="flex h-[36px] px-3 justify-center items-center rounded-r-lg border border-[#E5E7F0]"
              style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
              onClick={() => setIsOfferDeleteOpen(true)}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/deleteIcon.svg"}
                className={"w-4 h-4"}
              />
            </button>
          </div>
        </div>
        <div
          className="flex flex-col items-start gap-4 flex-1 min-w-0"
          style={{ maxHeight: "260px" }}
        >
          <div
            className="relative w-full"
            style={{
              paddingLeft: "50px",
              paddingBottom: "30px",
              height: "260px",
              maxHeight: "260px",
            }}
          >
            {/* Y-axis labels - outside the chart */}
            <div
              className="absolute left-0 top-0 flex flex-col justify-between"
              style={{
                width: "50px",
                height: "230px",
              }}
            >
              {[100, 80, 60, 40, 20, 0].map((value) => (
                <div
                  key={value}
                  className="text-[#8185A8] text-xs"
                  style={{
                    fontFamily: "Inter",
                    fontSize: "12px",
                    textAlign: "right",
                    paddingRight: "15px",
                  }}
                >
                  {value}
                </div>
              ))}
            </div>

            {/* Chart container with border and background */}
            <div
              className="relative"
              style={{
                borderRadius: "6px",
                border: "1px solid #E5E7F0",
                background: "linear-gradient(180deg, #FEFEFE 0%, #F9F9F9 100%)",
                padding: "12px",
                height: "230px",
              }}
            >
              {/* SVG Chart Container */}
              <div
                ref={chartContainerRef}
                className="relative w-full"
                style={{ height: "206px" }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{ overflow: "visible" }}
                >
                  {/* Line path */}
                  {chartData.length > 0 && (
                    <polyline
                      points={chartData
                        .map((item, index) => {
                          const x =
                            chartData.length > 1
                              ? (index / (chartData.length - 1)) * 100
                              : 50;
                          const y = 100 - (item.score / 100) * 100;
                          return `${x},${y}`;
                        })
                        .join(" ")}
                      fill="none"
                      stroke="#4A90E2"
                      strokeWidth="2"
                    />
                  )}

                  {/* Data points - using ellipse with aspect ratio compensation for perfect circles */}
                  {chartData.map((item, index) => {
                    const x =
                      chartData.length > 1
                        ? (index / (chartData.length - 1)) * 100
                        : 50;
                    const y = 100 - (item.score / 100) * 100;
                    const isLast = index === chartData.length - 1;
                    const baseRadius = isLast ? 2 : 1.5;
                    const rx = baseRadius / chartAspectRatio;
                    const ry = baseRadius;
                    return (
                      <ellipse
                        key={index}
                        cx={x}
                        cy={y}
                        rx={rx}
                        ry={ry}
                        fill="#4A90E2"
                        stroke="#fff"
                        strokeWidth="0.5"
                      />
                    );
                  })}
                </svg>
              </div>
            </div>

            {/* X-axis labels - outside the chart */}
            <div
              className="absolute bottom-0 left-[50px] right-0 flex justify-around items-center"
              style={{
                height: "30px",
              }}
            >
              {chartData.map((item, index) => (
                <div
                  key={index}
                  className="text-[#8185A8] text-xs"
                  style={{
                    fontFamily: "Inter",
                    fontSize: "12px",
                    textAlign: "center",
                  }}
                >
                  {item.week}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col flex-1 min-h-0 overflow-hidden">
        <div className="flex mb-[10px] h-[36px] pl-6 pr-[38px] flex-col items-start gap-[10px] self-stretch border-b border-[#E5E7F0] flex-shrink-0">
          <div className="flex items-center gap-[18px]">
            <button
              className={
                selectedTab === "email"
                  ? "OfferDetailsChosenTab"
                  : "OfferDetailsTab"
              }
              onClick={() => {
                setSelectedTab("email");
                setPage(1);
              }}
            >
              <img
                alt={"Logo"}
                src={
                  selectedTab === "email"
                    ? "/media/icons/stars-01-purple.svg"
                    : "/media/icons/stars-01.svg"
                }
                className={"w-3 h-3"}
              />
              Email Creative
            </button>

            <div className="w-[1px] self-stretch rounded bg-[#E5E7F0]"></div>
            <button
              className={
                selectedTab === "sms"
                  ? "OfferDetailsChosenTab"
                  : "OfferDetailsTab"
              }
              onClick={() => {
                setSelectedTab("sms");
                setPage(1);
              }}
            >
              <img
                alt={"Logo"}
                src={
                  selectedTab === "sms"
                    ? "/media/icons/stars-01-purple.svg"
                    : "/media/icons/stars-01.svg"
                }
                className={"w-3 h-3"}
              />
              SMS Creative
            </button>
            <div className="w-[1px] self-stretch rounded bg-[#E5E7F0]"></div>
            <button
              className={
                selectedTab === "lists"
                  ? "OfferDetailsChosenTab"
                  : "OfferDetailsTab"
              }
              onClick={() => {
                setSelectedTab("lists");
                setPage(1);
              }}
            >
              <img
                alt={"Logo"}
                src={
                  selectedTab === "lists"
                    ? "/media/icons/rows-03-purple.svg"
                    : "/media/icons/rows.svg"
                }
                className={"w-3 h-3"}
              />
              Lists
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {selectedTab === "sms" && (
            <SMSCreativeTable
              sortedList={smsCreatives}
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              setList={setSmsCreatives}
              list={smsCreatives}
              onOpenCreativeDelete={handleOpenCreativeDelete}
              onOpenNewCreative={handleOpenNewCreative}
              suggestions={offerDetails?.suggestions || []}
              onCreativeResolved={handleCreativeResolved}
              onCreativeNoted={handleCreativeNoted}
            />
          )}
          {selectedTab === "email" && (
            <EmailCreativeTable
              sortedList={emailCreatives}
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              setList={setEmailCreatives}
              list={emailCreatives}
              onOpenCreativeDelete={handleOpenCreativeDelete}
              onOpenNewCreative={handleOpenNewCreative}
              suggestions={offerDetails?.suggestions || []}
              onCreativeResolved={handleCreativeResolved}
              onCreativeNoted={handleCreativeNoted}
            />
          )}
          {selectedTab === "lists" && (
            <ListsTable
              sortedList={lists}
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              setList={setLists}
              list={lists}
            />
          )}
        </div>
        <div className="flex-shrink-0">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            padding="0 38px 0 24px"
          />
        </div>
      </div>
      {isNewCreativeOpen && (
        <NewCreative
          closeModal={closeModalAndRefresh}
          operationType={creativeOperationType}
          offerId={offerId}
          creative={selectedCreative}
        />
      )}
      {isOfferDeleteOpen && (
        <OfferDelete
          closeModal={handleOfferDeleteSuccess}
          setIsOfferDeleteOpen={setIsOfferDeleteOpen}
          offerId={offerId}
        />
      )}
      {isResetScoreOpen && (
        <ResetScore
          closeModal={closeModalAndRefresh}
          setIsResetScoreOpen={setIsResetScoreOpen}
          offerId={offerId}
          name={offerDetails?.offer?.name}
        />
      )}
      {isCreativeDeleteOpen && (
        <CreativeDelete
          closeModal={closeModalAndRefresh}
          creativeId={creativeId}
        />
      )}
      {isOfferEditOpen && (
        <NewOffer
          closeModal={closeModalAndRefresh}
          type={OperationType.UPDATE}
          offer={offerDetails?.offer}
        />
      )}
      {isAISuggestOpen && (
        <AISuggestCreativeModal
          offerId={offerId}
          onClose={() => setIsAISuggestOpen(false)}
          onSuccess={() => {
            setIsAISuggestOpen(false);
            refetchAllData();
          }}
          openToast={openToastWithType}
        />
      )}
    </div>
  );
};

export default injectIntl(Details);
